import type { ReactNode } from 'react'
import {
  Buyer,
  BuyerEditFormData,
  BuyerRegistrationFormData,
  BuyerRegistrationPublicPayload,
  CalculationConfig,
  ClientDemand,
  ComparisonStatFormData,
  Demand,
  DemandComparisonStatisticalResponse,
  DemandEditFormData
} from 'types/buyer'
import type {
  BestMatchData,
  ClientMatchesBody,
  ClientMatchesResponse,
  MatchData,
  MatchingOffers,
  ImbalanceDetails,
  MatchOptions,
  MatchTranche,
  MaximizerOptions,
  PublicMatchingOffers,
  RdnCalculationDetails,
  RdnPricingCapabilities,
  SearchMatchesData,
  SearchMatchesResponses
} from 'types/match'
import {
  BuiltOfferEditData,
  ClientOffer,
  Offer,
  PlannedOfferEditData,
  Seller,
  SellerEditFormData,
  SellerRegistrationFormData,
  SellerRegistrationPublicPayload
} from 'types/seller'

import type {
  Api,
  ClientPanelUser,
  ClientResourceBody,
  ClientType,
  Company,
  ConsentName,
  Consents,
  ConsentsResponse,
  ConsumptionDetails,
  Context,
  DemandsDetailsResponse,
  EnergyType,
  FieldErrors,
  GenerateStatisticDataFormData,
  OffersDetailsResponse,
  ProfileFileResponse,
  RegistrationResponse,
  ResourceType,
  SearchBody,
  SearchDemandsResponse,
  SearchFormData,
  SearchOffersResponse,
  SearchResponses,
  User
} from 'types'

import copy from 'copy.json'
import { basePaths } from 'paths'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { getXSRFToken, leaveOnlyNumbers, removeSpacesAndDashes } from 'utils'

const env = {
  dev:
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:3001'
      : 'https://web.develop.ozeos.pl',
  prod: '' // Set API address to the one where the front-end is hosted
}[(process.env.REACT_APP_API as 'dev' | 'prod') || 'dev']

const publicBaseUrl = `${env}/ozeos-service/public/api`
const adminBaseUrl = `${env}/ozeos-service/admin-panel/api`
const clientBaseUrl = `${env}/ozeos-service/client-panel/api`
export const accessBaseUrl = `${env}/ozeos-service/admin-panel/site`
export const clientAccessBaseUrl = `${env}/ozeos-service/client-panel/site`

const downloadFile = (blob: Blob, responseHeaders: Headers) => {
  const fileName = responseHeaders.get('Content-Disposition')?.split('=').pop() || ''
  const downloadUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = downloadUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
}

const initialLoadingState: Context['loading'] = {
  getUser: false,
  getLoginUser: false,
  getLoginUserClientPanel: false,
  registerUser: false,
  registerUserPublic: false,
  searchOffers: false,
  searchDemands: false
}

const Store = createContext<Context>({
  api: {
    getLoginUser: async () => null,
    searchResources: async () => null,
    registerSeller: async () => null,
    registerSellerPublic: async () => false,
    registerBuyer: async () => null,
    registerBuyerPublic: async () => false,
    getAdminClient: async () => null,
    editClient: async () => null,
    activateClient: async () => false,
    getResource: async () => null,
    getMatches: async () => null,
    getPublicMatches: async () => null,
    getRdnPricingCapabilities: async () => null,
    getRdnPricingDetails: async () => null,
    getImbalanceDetails: async () => null,
    reportInterest: async () => false,
    getIndividualEnergyPricingFile: async () => null,
    deleteResource: async () => false,
    deleteMatch: async () => false,
    addDemand: async () => null,
    editDemand: async () => null,
    addBuiltOffer: async () => null,
    addPlannedOffer: async () => null,
    editBuiltOffer: async () => null,
    editPlannedOffer: async () => null,
    activationResource: async () => false,
    searchClients: async () => null,
    searchMatches: async () => null,
    confirmEmail: async () => ({
      success: false,
      code: 500,
      message: 'Wystąpił nieznany błąd'
    }),
    getCity: async () => null,
    getCompany: async () => null,
    getProfileFiles: async () => null,
    putAdminProfileFile: async () => null,
    putClientProfileFile: async () => null,
    postProcessedResourceProfileFile: async () => null,
    acceptProcessedResourceProfileFile: async () => null,
    rejectProcessedResourceProfileFile: async () => null,
    findBestMatch: async () => null,
    calculateMatch: async () => null,
    changeMatchingState: async () => null,
    recalculateMatch: async () => null,
    maximizeTranche: async () => null,
    assignStatisticalProfile: async () => null,
    getConsents: async () => null,
    getConsumptionDetails: async () => null,
    getLoginUserClientPanel: async () => null,
    getClientDetails: async () => null,
    getClientResources: async () => null,
    getClientMatches: async () => null,
    getClientResourceDetails: async () => null,
    deleteClientResourceDetails: async () => false,
    getClientProfileFiles: async () => null,
    getCalculationConfig: async () => null,
    generateDemandComparisonStatistical: async () => null,
    assignToAgent: async () => null
  },
  currentUser: null,
  currentLoginUser: null,
  currentLoginUserClientPanel: null,
  offers: [],
  demands: [],
  loading: initialLoadingState,
  errors: [],
  errorCode: null,
  fieldErrors: {},
  getUser: async () => {},
  getLoginUser: async () => {},
  getLoginUserClientPanel: async () => {},
  registerUser: async () => null,
  registerUserPublic: async () => false,
  searchOffers: async () => {},
  searchDemands: async () => {},
  setErrors: () => {},
  setErrorCode: () => {},
  clearErrors: () => {},
  setFieldErrors: () => {},
  clearFieldErrors: (fieldName?: string) => {}
})

export const useStore = () => useContext(Store)

type Props = { children: ReactNode }

export function StoreProvider({ children }: Props) {
  const [currentUser, setCurrentUser] = useState<Context['currentUser']>(null)
  const [currentLoginUser, setCurrentLoginUser] = useState<Context['currentLoginUser']>(null)
  const [currentLoginUserClientPanel, setCurrentLoginUserClientPanel] =
    useState<Context['currentLoginUserClientPanel']>(null)
  const [offers, setOffers] = useState<Context['offers']>([])
  const [demands, setDemands] = useState<Context['demands']>([])
  const [loading, setLoading] = useState<Context['loading']>(initialLoadingState)
  const [errorCode, setErrorCode] = useState<number | null>(null) // Error code to be displayed in a modal
  const [errors, setErrors] = useState<string[]>([]) // General errors to be displayed in a modal
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({}) // Field errors to be displayed next to the input

  const handleErrors = async (
    response: Response,
    errorMessage = copy.validators.unknown_error
  ): Promise<null> => {
    let data: {
      field_errors?: FieldErrors
      errors?: string[]
    } = {}

    const pageIsNotPublic =
      window.location.pathname.startsWith(basePaths.client_panel) ||
      window.location.pathname.startsWith(basePaths.admin_panel)

    if (pageIsNotPublic && [401, 403].includes(response.status)) {
      const url = window.location.pathname.startsWith(basePaths.client_panel)
        ? clientAccessBaseUrl
        : accessBaseUrl
      window.location.replace(`${url}/login`)
      return null
    }

    try {
      data = await response.json()
    } catch (error) {
      setErrorCode(response.status)
      setErrors([copy.validators.response_parsing_error, String(error)])

      return null
    }

    setErrorCode(response.status)

    // If field_errors exists and it's not []
    if (data.field_errors && Object.keys(data.field_errors).length !== 0) {
      // Display field errors in inputs
      setFieldErrors(data.field_errors)
      // Additionally display modal if data.errors are returned from api
      if (data.errors) setErrors(data.errors)
    } else {
      // Display modal with data.errors from api or message
      setErrors(data.errors || [errorMessage])
    }

    return null
  }

  const clearErrors = useCallback(() => {
    setErrors([])
    setErrorCode(null)
  }, [])

  const clearFieldErrors = useCallback((fieldName?: string) => {
    if (fieldName) {
      setFieldErrors(state => {
        const newState = { ...state }
        delete newState[fieldName]
        return newState
      })
    } else {
      setFieldErrors({})
    }
  }, [])

  const api = useMemo<Api>(
    () => ({
      getLoginUser: async (): Promise<{ login: string } | null> => {
        const response = await fetch(`${adminBaseUrl}/users/me`, { method: 'GET' })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy logowaniu')
      },
      searchResources: async (
        resourceType: 'offers' | 'demands',
        clientId?: number,
        formData: SearchBody = { page_number: 0 }
      ): Promise<(SearchOffersResponse & SearchDemandsResponse) | null> => {
        if (!clientId && formData?.statuses?.length === 0) return null

        if (resourceType === 'offers') formData.seller_id = clientId
        if (resourceType === 'demands') formData.buyer_id = clientId

        const response = await fetch(`${adminBaseUrl}/${resourceType}/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy wyszukiwaniu ${resourceType === 'offers' ? 'ofert' : 'zapotrzebowań'}`
        )
      },
      registerSeller: async (
        formData: SellerRegistrationFormData
      ): Promise<RegistrationResponse | null> => {
        const response = await fetch(`${adminBaseUrl}/sellers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy rejestracji sprzedającego')
      },
      registerSellerPublic: async (formData: SellerRegistrationPublicPayload) => {
        const response = await fetch(`${publicBaseUrl}/sellers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return true

        handleErrors(response, 'Błąd przy rejestracji sprzedającego')

        return false
      },
      registerBuyer: async (
        formData: BuyerRegistrationFormData
      ): Promise<RegistrationResponse | null> => {
        const response = await fetch(`${adminBaseUrl}/buyers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getXSRFToken()
          },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy rejestracji kupującego')
      },
      registerBuyerPublic: async (formData: BuyerRegistrationPublicPayload) => {
        const response = await fetch(`${publicBaseUrl}/buyers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return true

        handleErrors(response, 'Błąd przy rejestracji kupującego')

        return false
      },
      getAdminClient: async (
        clientType: ClientType,
        id: number
      ): Promise<(Buyer & Seller) | null> => {
        const response = await fetch(`${adminBaseUrl}/${clientType}s/${id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        const clientCopy = clientType === 'buyer' ? 'kupującego' : 'sprzedającego'

        if (response.status === 200) return await response.json()
        if (response.status === 404) {
          setErrors([`Nie znaleziono ${clientCopy} z id ${id}`])
          return null
        }

        return handleErrors(response, `Błąd przy wyszukiwaniu ${clientCopy}`)
      },
      editClient: async (
        clientType: ClientType,
        id: number,
        formData: BuyerEditFormData | SellerEditFormData,
        isClient: boolean
      ): Promise<null | 'IS_CONFLICT'> => {
        const url = isClient
          ? `${clientBaseUrl}/${clientType}s/me`
          : `${adminBaseUrl}/${clientType}s/${id}`

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getXSRFToken()
          },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        if (response.status === 409) {
          handleErrors(response)
          return 'IS_CONFLICT'
        }

        return handleErrors(
          response,
          `Błąd przy edycji ${clientType === 'buyer' ? 'kupującego' : 'sprzedającego'}`
        )
      },
      activateClient: async (clientType: ClientType, id: number) => {
        const response = await fetch(`${adminBaseUrl}/${clientType}s/${id}:activate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
        })

        if (response.status === 200) return true

        handleErrors(
          response,
          `Błąd przy aktywacji ${clientType === 'buyer' ? 'kupującego' : 'sprzedającego'}`
        )

        return false
      },
      getResource: async (
        resourceType: 'offers' | 'demands',
        resourceId: number
      ): Promise<(Offer & Demand) | null> => {
        const response = await fetch(`${adminBaseUrl}/${resourceType}/${resourceId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy pobieraniu szczegółów ${
            resourceType === 'offers' ? 'oferty' : 'zapotrzebowania'
          }`
        )
      },
      getMatches: async (matchId: number): Promise<MatchingOffers | null> => {
        const response = await fetch(`${adminBaseUrl}/matches/${matchId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return await response.json()
        if (response.status === 404) {
          setErrors([`Nie znaleziono porównania z id ${matchId}`])
          return null
        }

        return handleErrors(response, 'Błąd przy pobieraniu porównań')
      },
      getRdnPricingCapabilities: async (): Promise<RdnPricingCapabilities | null> => {
        const response = await fetch(`${adminBaseUrl}/matches/rdn-pricing-capabilities`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        if (response.status === 200) return await response.json()
        return handleErrors(response, 'Błąd przy pobieraniu dostępnych metod cenowych RDN')
      },
      getRdnPricingDetails: async (
        id: string,
        isPublic: boolean
      ): Promise<RdnCalculationDetails | null> => {
        const baseUrl = isPublic ? publicBaseUrl : adminBaseUrl
        const response = await fetch(`${baseUrl}/matches/${id}/rdn-pricing-details`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        if (response.status === 200) return await response.json()
        return handleErrors(response, 'Błąd przy pobieraniu szczegółów cen RDN')
      },
      getImbalanceDetails: async (
        id: string,
        isPublic: boolean
      ): Promise<ImbalanceDetails | null> => {
        const baseUrl = isPublic ? publicBaseUrl : adminBaseUrl
        const response = await fetch(`${baseUrl}/matches/${id}/imbalance-details`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        if (response.status === 200) return await response.json()
        return handleErrors(response, 'Błąd przy pobieraniu szczegółów niedoborów i nadwyżek')
      },
      getPublicMatches: async (diffId: string): Promise<PublicMatchingOffers | null> => {
        const response = await fetch(`${publicBaseUrl}/matches/${diffId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy pobieraniu porównań')
      },
      reportInterest: async (diffId: string) => {
        const response = await fetch(`${publicBaseUrl}/matches/${diffId}:report-interest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return true

        handleErrors(response, 'Błąd przy zgłaszaniu zainteresowania')

        return false
      },
      getIndividualEnergyPricingFile: async (id: string, isPublic?: boolean): Promise<null> => {
        if (!id) return null

        const base = isPublic ? publicBaseUrl : adminBaseUrl
        const url = `${base}/matches/${id}/individual-energy-pricing`

        const response = await fetch(url, { method: 'GET' })

        if (response.status === 200) {
          const blob = await response.blob()
          downloadFile(blob, response.headers)
          return null
        }

        return handleErrors(response, `Błąd przy pobieraniu pliku indywidualnej ścieżki cenowej`)
      },
      deleteResource: async (
        resourceType: 'offers' | 'demands',
        resourceId: number,
        reason: string
      ) => {
        const response = await fetch(`${adminBaseUrl}/${resourceType}/${resourceId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify({ reason })
        })

        if (response.status === 200) return true

        handleErrors(
          response,
          `Błąd przy usuwaniu ${resourceType === 'offers' ? 'oferty' : 'zapotrzebowania'}`
        )

        return false
      },
      deleteMatch: async (matchId: number, reason: string) => {
        const response = await fetch(`${adminBaseUrl}/matches/${matchId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify({ reason })
        })

        if (response.status === 200) return true

        handleErrors(response, 'Błąd przy usuwaniu porównań')

        return false
      },
      addDemand: async (
        isClientData: boolean,
        formData: FormData
      ): Promise<{ id: number } | null> => {
        const url = isClientData ? `${clientBaseUrl}/demands/my` : `${adminBaseUrl}/demands`
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'X-XSRF-TOKEN': getXSRFToken() },
          body: formData
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy dodawaniu zapotrzebowania')
      },
      editDemand: async (
        id: string,
        isClientData: boolean,
        formData: DemandEditFormData
      ): Promise<DemandEditFormData | null | 'IS_CONFLICT'> => {
        const url = isClientData
          ? `${clientBaseUrl}/demands/my/${id}`
          : `${adminBaseUrl}/demands/${id}`

        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        if (response.status === 409) {
          handleErrors(response, 'Błąd przy edytowaniu zapotrzebowania')
          return 'IS_CONFLICT'
        }

        return handleErrors(response, 'Błąd przy edytowaniu zapotrzebowania')
      },
      addBuiltOffer: async (
        isClientData: boolean,
        formData: FormData
      ): Promise<{ id: number } | null> => {
        const url = isClientData
          ? `${clientBaseUrl}/offers/my/built-installation`
          : `${adminBaseUrl}/offers/built-installation`
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'X-XSRF-TOKEN': getXSRFToken() },
          body: formData
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy dodawaniu oferty')
      },
      editBuiltOffer: async (
        id: string,
        isClientData: boolean,
        formData: BuiltOfferEditData
      ): Promise<BuiltOfferEditData | null | 'IS_CONFLICT'> => {
        const url = isClientData
          ? `${clientBaseUrl}/offers/my/built-installation/${id}`
          : `${adminBaseUrl}/offers/built-installation/${id}`

        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        if (response.status === 409) {
          handleErrors(response)
          return 'IS_CONFLICT'
        }

        return handleErrors(response, 'Błąd przy edytowaniu oferty')
      },
      addPlannedOffer: async (
        isClientData: boolean,
        formData: any
      ): Promise<{ id: number } | null> => {
        const url = isClientData
          ? `${clientBaseUrl}/offers/my/planned-installation`
          : `${adminBaseUrl}/offers/planned-installation`

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: formData
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy dodawaniu oferty')
      },
      editPlannedOffer: async (
        id: string,
        isClientData: boolean,
        formData: PlannedOfferEditData
      ): Promise<PlannedOfferEditData | null | 'IS_CONFLICT'> => {
        const url = isClientData
          ? `${clientBaseUrl}/offers/my/planned-installation/${id}`
          : `${adminBaseUrl}/offers/planned-installation/${id}`

        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        if (response.status === 409) {
          handleErrors(response)
          return 'IS_CONFLICT'
        }

        return handleErrors(response, 'Błąd przy edytowaniu oferty')
      },
      activationResource: async (
        resourceType: ResourceType,
        resourceId: number,
        purpose: 'activate' | 'deactivate',
        reason = 'Aktywacja'
      ) => {
        const response = await fetch(`${adminBaseUrl}/${resourceType}s/${resourceId}:${purpose}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify({ reason })
        })

        if (response.status === 200) return true

        handleErrors(
          response,
          `Błąd przy ${purpose === 'activate' ? 'aktywacji' : 'deaktywacji'} ${
            resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'
          }`
        )

        return false
      },
      searchClients: async (
        client: ClientType,
        searchFormData: SearchFormData
      ): Promise<SearchResponses | null> => {
        const response = await fetch(`${adminBaseUrl}/${client}s/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(searchFormData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy wyszukiwaniu ${client === 'seller' ? 'sprzedających' : 'kupujących'}`
        )
      },
      searchMatches: async (
        searchFormData: SearchMatchesData
      ): Promise<SearchMatchesResponses | null> => {
        const response = await fetch(`${adminBaseUrl}/matches/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(searchFormData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy wyszukiwaniu porównań')
      },
      confirmEmail: async (
        id = ''
      ): Promise<{ success: boolean; code: number; message: string }> => {
        if (!id) return { success: false, code: 0, message: 'Brak id' }

        const response = await fetch(`${publicBaseUrl}/email-verification-process/${id}:finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
        })

        const code = response.status

        // These messages here are for us as a form of documentation, because backend doesn't return any messages
        if (code === 200) return { success: true, code, message: 'Email został zweryfikowany' }
        if (code === 400) return { success: false, code, message: 'Niepoprawny numer ID' }
        if (code === 404) return { success: false, code, message: 'Nie odnaleziono numeru ID' }
        if (code === 422) return { success: false, code, message: 'Nieaktualny numer ID' }
        return { success: false, code, message: 'Błąd serwera' }
      },
      getCity: async (zipCode: string, isClientPanel?: boolean): Promise<string | null> => {
        if (!zipCode) return null

        const baseUrl = isClientPanel ? clientBaseUrl : adminBaseUrl

        const response = await fetch(`${baseUrl}/zip-codes/${leaveOnlyNumbers(zipCode)}/cities`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
        })

        if (response.status === 200) {
          const data: string[] = await response.json()
          if (data[0]) return data[0]
        }

        return null
      },
      getCompany: async (taxIdentifier: string): Promise<Company | null> => {
        if (!taxIdentifier) return null

        taxIdentifier = taxIdentifier.replace(/[\s-]/gi, '')

        const response = await fetch(`${publicBaseUrl}/companies?taxIdentifier=${taxIdentifier}`, {
          method: 'GET'
        })

        return response.status === 200 ? await response.json() : null
      },
      getProfileFiles: async (
        type: 'offers' | 'demands',
        profileFileType: 'original' | 'processed',
        id: number
      ): Promise<null> => {
        if (!id) return null

        const response = await fetch(
          `${adminBaseUrl}/${type}/${id}/profile-file/${profileFileType}`,
          { method: 'GET' }
        )

        if (response.status === 200) {
          const blob = await response.blob()
          downloadFile(blob, response.headers)

          return null
        }

        return handleErrors(
          response,
          `Błąd przy pobieraniu pliku ${type === 'offers' ? 'oferty' : 'zapotrzebowania'}`
        )
      },
      putAdminProfileFile: async (
        formData: FormData,
        resourceType: ResourceType,
        resourceId: number
      ) => {
        const response = await fetch(
          `${adminBaseUrl}/${resourceType}s/${resourceId}/profile-file/original`,
          {
            method: 'PUT',
            headers: { 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData
          }
        )
        if (response.status === 200) return await response.json()

        return handleErrors(response, `Błąd przy dodawaniu pliku profilowego`)
      },
      putClientProfileFile: async (
        formData: FormData,
        resourceType: ResourceType,
        resourceId: string
      ) => {
        const response = await fetch(
          `${clientBaseUrl}/${resourceType}s/my/${resourceId}/profile-file/original`,
          {
            method: 'PUT',
            headers: { 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData
          }
        )
        if (response.status === 200) return await response.json()

        return handleErrors(response, `Błąd przy dodawaniu pliku profilowego`)
      },
      postProcessedResourceProfileFile: async (
        formData: FormData,
        resourceType: ResourceType,
        resourceId: number
      ): Promise<ProfileFileResponse | null> => {
        const response = await fetch(
          `${adminBaseUrl}/${resourceType}s/${resourceId}/profile-file/processed:read`,
          {
            method: 'POST',
            headers: { 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData
          }
        )

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy dodawaniu obrobionego profilu ${
            resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'
          }`
        )
      },
      acceptProcessedResourceProfileFile: async (
        resourceType: ResourceType,
        resourceId: number,
        applyMultiplier: boolean
      ): Promise<null> => {
        const requestBody = {
          apply_multiplier: applyMultiplier
        }

        const response = await fetch(
          `${adminBaseUrl}/${resourceType}s/${resourceId}/profile-file/processed:accept`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
            body: JSON.stringify(requestBody)
          }
        )

        if (response.status === 200) return null

        return handleErrors(
          response,
          `Błąd przy akceptowaniu obrobionego profilu ${
            resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'
          }`
        )
      },
      rejectProcessedResourceProfileFile: async (
        resourceType: ResourceType,
        resourceId: number
      ): Promise<null> => {
        const response = await fetch(
          `${adminBaseUrl}/${resourceType}s/${resourceId}/profile-file/processed:reject`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
          }
        )

        if (response.status === 200) return null

        return handleErrors(
          response,
          `Błąd przy odrzucaniu obrobionego profilu ${
            resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'
          }`
        )
      },
      findBestMatch: async (
        demandId: number,
        formData: FormData
      ): Promise<BestMatchData | null> => {
        const response = await fetch(
          `${adminBaseUrl}/matches/demands/${demandId}:calculate-best-match`,
          {
            method: 'POST',
            headers: { 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData
          }
        )

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy znajdowaniu pasujących ofert')
      },
      calculateMatch: async (demandId: number, formData: FormData): Promise<MatchData | null> => {
        const response = await fetch(
          `${adminBaseUrl}/matches/demands/${demandId}:calculate-match`,
          {
            method: 'POST',
            headers: { 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData
          }
        )

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy znajdowaniu pasujących ofert')
      },
      changeMatchingState: async (
        matchId: number,
        actionType: 'accept' | 'finalize' | 'reserve' | 'uno-accept' | 'uno-reserve',
        reason: string,
        numberOfDays?: number
      ): Promise<null> => {
        const body: { reason: string; number_of_days?: number } = { reason }
        if (numberOfDays) body.number_of_days = numberOfDays

        const response = await fetch(`${adminBaseUrl}/matches/${matchId}:${actionType}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(body)
        })

        if (response.status === 200) return null

        return handleErrors(response, 'Błąd przy zmianie statusu porównania')
      },
      recalculateMatch: async (
        matchId: number,
        offerOptions: MatchOptions & MatchTranche
      ): Promise<MatchData | null> => {
        const response = await fetch(`${adminBaseUrl}/matches/${matchId}:recalculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(offerOptions)
        })

        if (response.status === 200) return response.json()

        return handleErrors(response, 'Błąd przy przeliczeniu porównania')
      },
      maximizeTranche: async (
        matchId: number,
        offerOptions: MaximizerOptions & MatchTranche
      ): Promise<(MaximizerOptions & MatchTranche) | null> => {
        const response = await fetch(`${adminBaseUrl}/matches/${matchId}:maximize-tranche`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(offerOptions)
        })

        if (response.status === 200) return response.json()

        return handleErrors(response, 'Błąd przy maksymalizacji transzy porównania')
      },
      assignStatisticalProfile: async (
        offerId: number,
        energyType: Exclude<EnergyType, 'HYDRO'>,
        formData?: GenerateStatisticDataFormData
      ): Promise<null> => {
        const response = await fetch(
          `${adminBaseUrl}/offers/${offerId}/type/${energyType}:assign-statistical-profile`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
            body: formData ? JSON.stringify(formData) : null
          }
        )

        if (response.status === 200) return null

        return handleErrors(response, 'Błąd przy generowaniu danych statystycznych')
      },
      getConsents: async (clientType: ClientType): Promise<Consents | null> => {
        const response = await fetch(
          `${publicBaseUrl}/consents?clientType=${clientType.toUpperCase()}`,
          { method: 'GET' }
        )

        if (response.status === 200) {
          const data: ConsentsResponse = await response.json()

          if (data.consents) {
            return Object.entries(data.consents).map(([key, value]) => ({
              name: key as ConsentName,
              ...value
            }))
          }

          return handleErrors(response, 'Format zgód jest nieprawidłowy')
        }

        return handleErrors(response, 'Błąd przy pobieraniu zgód')
      },
      getConsumptionDetails: async (
        id: string,
        isClientData: boolean,
        isPublicPage?: boolean
      ): Promise<ConsumptionDetails | null> => {
        const url = isClientData
          ? `${clientBaseUrl}/demands/my/${id}/consumption-details`
          : isPublicPage
          ? `${publicBaseUrl}/matches/${id}/consumption-details`
          : `${adminBaseUrl}/demands/${id}/consumption-details`

        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return response.json()

        return handleErrors(response, 'Błąd przy pobieraniu profilu energetycznego firmy')
      },
      getLoginUserClientPanel: async (): Promise<ClientPanelUser | null> => {
        const response = await fetch(`${clientBaseUrl}/users/me`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy pobieraniu danych kilenta')
      },
      getClientDetails: async (clientType: ClientType): Promise<(Buyer & Seller) | null> => {
        const response = await fetch(`${clientBaseUrl}/${clientType}s/me/details`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        const clientCopy = clientType === 'buyer' ? 'kupującego' : 'sprzedającego'

        if (response.status === 200) return await response.json()
        if (response.status === 404) {
          setErrors([`Nie znaleziono ${clientCopy}`])
          return null
        }

        return handleErrors(response, `Błąd przy pobieraniu danych ${clientCopy}`)
      },
      getClientResources: async (
        resourceType: ResourceType,
        formData: ClientResourceBody
      ): Promise<(OffersDetailsResponse & DemandsDetailsResponse) | null> => {
        const response = await fetch(`${clientBaseUrl}/${resourceType}s/my/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy pobieraniu ${resourceType === 'offer' ? 'ofert' : 'zapotrzebowań'}`
        )
      },
      getClientMatches: async (
        id: string,
        formData: ClientMatchesBody
      ): Promise<ClientMatchesResponse | null> => {
        const response = await fetch(`${clientBaseUrl}/matches/my/demands/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
          body: JSON.stringify(formData)
        })

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy pobieraniu pasujących zapotrzebowań')
      },
      getClientResourceDetails: async (
        resourceType: ResourceType,
        id: string
      ): Promise<(ClientOffer & ClientDemand) | null> => {
        const response = await fetch(`${clientBaseUrl}/${resourceType}s/my/${id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })

        if (response.status === 200) return await response.json()

        return handleErrors(
          response,
          `Błąd przy pobieraniu szczegółów ${
            resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'
          }`
        )
      },
      deleteClientResourceDetails: async (resourceType: ResourceType, id: string) => {
        const response = await fetch(`${clientBaseUrl}/${resourceType}s/my/${id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
        })

        if (response.status === 200) return true

        handleErrors(
          response,
          `Błąd przy usuwaniu ${resourceType === 'offer' ? 'oferty' : 'zapotrzebowania'}`
        )

        return false
      },
      getClientProfileFiles: async (type: ResourceType, id: string): Promise<null> => {
        if (!id) return null

        const response = await fetch(`${clientBaseUrl}/${type}s/my/${id}/profile-file/original`, {
          method: 'GET'
        })

        if (response.status === 200) {
          const blob = await response.blob()
          downloadFile(blob, response.headers)

          return null
        }

        return handleErrors(
          response,
          `Błąd przy pobieraniu pliku ${type === 'offer' ? 'oferty' : 'zapotrzebowania'}`
        )
      },
      getCalculationConfig: async (id: string): Promise<CalculationConfig | null> => {
        const response = await fetch(
          `${clientBaseUrl}/matches/my/demands/${id}/calculation-config`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          }
        )

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy pobieraniu konfiguracji')
      },
      generateDemandComparisonStatistical: async (
        id: string,
        formData: ComparisonStatFormData
      ): Promise<DemandComparisonStatisticalResponse | null> => {
        const response = await fetch(
          `${clientBaseUrl}/matches/my/demands/${id}/calculate-statistical`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() },
            body: JSON.stringify(formData)
          }
        )

        if (response.status === 200) return await response.json()

        return handleErrors(response, 'Błąd przy generowaniu porównania')
      },
      assignToAgent: async (id: number, clientType: ClientType): Promise<null> => {
        const response = await fetch(`${adminBaseUrl}/${clientType}s/${id}:assign-to-me`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getXSRFToken() }
        })

        if (response.status === 200) return null

        return handleErrors(response, 'Błąd przy przypisywaniu do agenta')
      }
    }),
    []
  )

  const registerUser = useCallback(
    async (
      email: User['email'],
      userType: User['type'],
      formData: BuyerRegistrationFormData | SellerRegistrationFormData
    ) => {
      if (loading.registerUser) return null

      setLoading(state => ({ ...state, registerUser: true }))

      formData.tax_identifier = removeSpacesAndDashes(formData.tax_identifier)
      formData.phone_number = removeSpacesAndDashes(formData.phone_number)

      const response = await (userType === 'buyer'
        ? api.registerBuyer(formData as BuyerRegistrationFormData)
        : api.registerSeller(formData as SellerRegistrationFormData))

      if (response) setCurrentUser({ id: response.id, type: userType, email })

      setLoading(state => ({ ...state, registerUser: false }))

      return response
    },
    [api, loading.registerUser]
  )

  const registerUserPublic = useCallback(
    async (
      clientType: ClientType,
      payload: BuyerRegistrationPublicPayload | SellerRegistrationPublicPayload
    ) => {
      if (loading.registerUserPublic) return false

      setLoading(state => ({ ...state, registerUserPublic: true }))

      payload.tax_identifier = removeSpacesAndDashes(payload.tax_identifier)
      payload.phone_number = removeSpacesAndDashes(payload.phone_number)

      const response = await (clientType === 'buyer'
        ? api.registerBuyerPublic(payload as BuyerRegistrationPublicPayload)
        : api.registerSellerPublic(payload as SellerRegistrationPublicPayload))

      setLoading(state => ({ ...state, registerUserPublic: false }))

      return response
    },
    [api, loading.registerUserPublic]
  )

  const getUser = useCallback(
    async (userType: User['type'], id: User['id']) => {
      if (loading.getUser) return

      setLoading(state => ({ ...state, getUser: true }))

      const user = (await api.getAdminClient(userType, id)) as (Buyer & Seller) | null

      if (user) {
        setCurrentUser({ id: user.id, type: userType, email: user.contact_email })
      }

      setLoading(state => ({ ...state, getUser: false }))
    },
    [api, loading.getUser]
  )

  const getLoginUser = useCallback(async () => {
    if (loading.getLoginUser) return

    setLoading(state => ({ ...state, getLoginUser: true }))

    const user = await api.getLoginUser()

    if (user?.login) {
      setCurrentLoginUser({ login: user.login })
    }

    setLoading(state => ({ ...state, getLoginUser: false }))
  }, [api, loading.getLoginUser])

  const getLoginUserClientPanel = useCallback(async () => {
    if (loading.getLoginUserClientPanel) return

    setLoading(state => ({ ...state, getLoginUserClientPanel: true }))

    const userClientPanel = await api.getLoginUserClientPanel()

    if (userClientPanel?.business_id) {
      setCurrentLoginUserClientPanel(userClientPanel)
    }

    setLoading(state => ({ ...state, getLoginUserClientPanel: false }))
  }, [api, loading.getLoginUserClientPanel])

  const searchOffers = useCallback(
    async (userId = currentUser?.id, formData?: SearchBody) => {
      if (loading.searchOffers) return

      setLoading(state => ({ ...state, searchOffers: true }))

      const response = await api.searchResources('offers', userId, formData)

      if (response) setOffers(response.content)

      setLoading(state => ({ ...state, searchOffers: false }))
    },
    [api, currentUser?.id, loading.searchOffers]
  )

  const searchDemands = useCallback(
    async (userId = currentUser?.id, formData?: SearchBody) => {
      if (loading.searchDemands) return

      setLoading(state => ({ ...state, searchDemands: true }))

      const response = await api.searchResources('demands', userId, formData)

      if (response) setDemands(response.content)

      setLoading(state => ({ ...state, searchDemands: false }))
    },
    [api, currentUser?.id, loading.searchDemands]
  )

  return (
    <Store.Provider
      value={{
        api,
        currentUser,
        currentLoginUser,
        currentLoginUserClientPanel,
        offers,
        demands,
        loading,
        errors,
        errorCode,
        fieldErrors,
        getUser,
        getLoginUser,
        getLoginUserClientPanel,
        registerUser,
        registerUserPublic,
        searchOffers,
        searchDemands,
        setErrors,
        setErrorCode,
        clearErrors,
        setFieldErrors,
        clearFieldErrors
      }}
    >
      {children}
    </Store.Provider>
  )
}
